import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from '../../../../views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from '../../../../helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from '../../../../helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from '../../../../components/Grid/Grid2';
import { Loader } from '../../../../components/Loader/Loader';
import { FILTER_TYPE, FIRST_PAGE } from '../../../../consts/table';
import { SimpleModal } from '../../../../components/SimpleModal/SimpleModal';
import { Tournament } from '../../../../models/tournament';
import { TournamentWaitingListItem } from '../../../../models/tournamentWaitingList';
import { getTournament } from '../../../../helpers/service/adminSU/tournamentsTable';
import {
  getAllTournamentWaitingList,
  getTournamentWaitingList,
  getTournamentWaitingListCount,
  removeTournamentWaitingListSchool
} from '../../../../helpers/service/adminSU/tournamentWaitingListTable';

const formatDateTime = (raw?: string) => {
  if (!raw) return '—';

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';

  const pad2 = (n: number) => (String(n).length >= 2 ? String(n) : `0${n}`);

  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}`;
};

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Position',
    field: 'waitingListPosition',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['waitingListPosition']
  },
  {
    text: 'Joined waiting list',
    field: 'waitingListJoinedAt',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (item: TournamentWaitingListItem): JSX.Element => {
      return <td>{formatDateTime(item.waitingListJoinedAt || propz.get(item, ['invite', 'statusSetAt']))}</td>;
    }
  },
  {
    text: 'Invite status',
    field: 'inviteStatus',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (item: TournamentWaitingListItem): JSX.Element => {
      return <td>{String(propz.get(item, ['invite', 'status'], '') || '—')}</td>;
    }
  }
];

interface State {
  items: TournamentWaitingListItem[];
  currentPage: number;
  selectedItems: TournamentWaitingListItem[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  tournament: Tournament;
  isRemoveModalOpen: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class TournamentWaitingList extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      tournament: undefined,
      isRemoveModalOpen: false
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true });

    this.setAdditionalItems().then(() => {
      this.setItems();
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true });
      this.setItems();
    }
  }

  getTournamentId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    return search.tournament as string;
  }

  setAdditionalItems() {
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    return getTournament(user, tournamentId).then(tournament => {
      this.setState({ tournament });
      return true;
    });
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortColumnsName !== '' && sortDirection !== '' ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const tournamentId = this.getTournamentId();

    return BPromise.all([
      getTournamentWaitingListCount(user, tournamentId, where),
      getTournamentWaitingList(user, tournamentId, serverQueryFilter)
    ]).then(([countObj, items]) => {
      const total = Number(propz.get(countObj, ['count'], 0)) || 0;
      const nextItems: TournamentWaitingListItem[] = Array.isArray(items) ? items : [];

      this.setState({
        currentPage: page,
        sortDirection,
        sortColumnsName,
        isShowFilter,
        isDataFiltered,
        filters,
        items: nextItems,
        total,
        basePath: history.location.pathname,
        isLoading: false
      });
      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search = [];
    const { filters, sortColumnsName, sortDirection } = this.state;
    const { history } = this.props;
    const tournamentId = this.getTournamentId();

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const { user } = this.props;
    const where = getServerFieldSectionWhere(filters);
    const tournamentId = this.getTournamentId();

    this.setState({ isLoading: true });

    getAllTournamentWaitingList(user, tournamentId, where).then(items => {
      const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

      this.setState({
        selectedItems: [...selectedItems, ...itemsFiltered],
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const { history } = this.props;
    const tournamentId = this.getTournamentId();

    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onTableFilterChange = (event, filterField: string): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;

    this.setState({
      filters: {
        ...filters,
        [filterField]: filterValue
      }
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const { history } = this.props;
    const tournamentId = this.getTournamentId();
    const search = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onClearFilterClick = (): void => {
    const { history } = this.props;
    const tournamentId = this.getTournamentId();

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}`,
      state: history.location.state
    });
  };

  onCancelFilterClick = (): void => {
    this.setState({ isShowFilter: false, filters: getFilters2(COLUMNS, parse(this.props.history.location.search)) });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState(prevState => ({
      isShowFilter: !prevState.isShowFilter
    }));
  };

  openRemoveModal = (): void => {
    this.setState({
      isRemoveModalOpen: true
    });
  };

  closeRemoveModal = (): void => {
    this.setState({
      isRemoveModalOpen: false
    });
  };

  removeFromWaitingList = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    this.setState({
      isLoading: true
    });

    BPromise.each(selectedItems, item => {
      return removeTournamentWaitingListSchool(user, tournamentId, item.id);
    }).then(() => {
      this.setState({
        isRemoveModalOpen: false,
        selectedItems: [],
        isSelectAllChecked: false
      });
      this.setItems();
    });
  };

  goBack = () => {
    const { history, location } = this.props;
    const { state } = location;
    const { search, prevPath } = (state || {}) as any;
    const tournamentId = this.getTournamentId();

    history.push({
      pathname: prevPath,
      search,
      state: { tournamentId }
    });
  };

  renderRemoveModal(): React.ReactNode {
    const { selectedItems, isRemoveModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isRemoveModalOpen}
        title={'Remove from waiting list'}
        body={`Are you sure you want to remove ${
          selectedItems.length === 1 ? 'this school' : 'these schools'
        } from the waiting list?`}
        buttonText={'Remove'}
        onButtonClick={this.removeFromWaitingList}
        onCloseClick={this.closeRemoveModal}
      />
    );
  }

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      tournament
    } = this.state;

    const actionItems = [
      {
        itemText: 'Remove from waiting list',
        onItemClick: this.openRemoveModal,
        isActive: selectedItems.length > 0
      }
    ];

    const gridTitle = typeof tournament !== 'undefined' ? `${tournament.name} / Waiting list` : '';

    return (
      <Grid2
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={{}}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onCancelFilterClick={this.onCancelFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        gridTitle={gridTitle}
        goBack={this.goBack}
        user={this.props.user}
      />
    );
  }

  render() {
    const { isLoading, isRemoveModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isRemoveModalOpen ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {this.renderRemoveModal()}

        <div className="row">
          <div className="col-md-12">{this.renderGrid()}</div>
        </div>
      </div>
    );
  }
}

